// src/utils/planPermissions.ts
// Helper centralizado para verificar permisos según el plan de suscripción.
// Jerarquía: premium > pro > free/onboarding

import type { Subscription, PlanType } from '../types/user';

// Nivel numérico de cada plan (mayor = más acceso)
const PLAN_HIERARCHY: Record<PlanType, number> = {
  free: 0,
  onboarding: 0,
  pro: 1,
  premium: 2,
};

/**
 * Verifica si la suscripción del usuario tiene al menos el nivel de plan requerido.
 * Un usuario premium pasa también las verificaciones de pro y free.
 * 
 * Ejemplo:
 *   hasPlanAccess(user.subscription, 'pro')     // true si es pro O premium
 *   hasPlanAccess(user.subscription, 'premium')  // true SOLO si es premium
 */
export function hasPlanAccess(
  subscription: Subscription | undefined | null,
  requiredPlan: PlanType
): boolean {
  // Sin suscripción o inactiva = solo acceso free
  if (!subscription || subscription.status !== 'active') {
    return requiredPlan === 'free' || requiredPlan === 'onboarding';
  }
  return PLAN_HIERARCHY[subscription.plan] >= PLAN_HIERARCHY[requiredPlan];
}

/**
 * Shortcut: ¿Tiene acceso PRO (o superior)?
 * Un usuario premium también retorna true aquí.
 */
export function isPro(subscription: Subscription | undefined | null): boolean {
  return hasPlanAccess(subscription, 'pro');
}

/**
 * Shortcut: ¿Tiene acceso PREMIUM?
 */
export function isPremium(subscription: Subscription | undefined | null): boolean {
  return hasPlanAccess(subscription, 'premium');
}

/**
 * Retorna el label legible del plan para mostrar en UI.
 */
export function getPlanLabel(plan: PlanType): string {
  const labels: Record<PlanType, string> = {
    free: 'Gratis',
    onboarding: 'Onboarding',
    pro: 'PRO',
    premium: 'Premium',
  };
  return labels[plan] || 'Gratis';
}

/**
 * Retorna las clases CSS del badge según el plan.
 * Útil para badges en tablas de admin, perfiles, etc.
 */
export function getPlanBadgeClasses(plan: PlanType): string {
  const classes: Record<PlanType, string> = {
    free: 'bg-canvas text-content',
    onboarding: 'bg-blue-100 dark:bg-blue-500/20 text-blue-800 dark:text-blue-300',
    pro: 'bg-yellow-100 dark:bg-yellow-500/20 text-yellow-800 dark:text-yellow-300',
    premium: 'bg-purple-100 dark:bg-purple-500/20 text-purple-800 dark:text-purple-300',
  };
  return classes[plan] || classes.free;
}